import { useEffect, useState } from 'react';
import './Slider.css';
import {
  TrashIcon,
  VariableIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { GreaterThanEqualsIcon } from '../icons';

export const RiskScoreFilter = ({
  id,
  label,
  setValue,
  hideIcon,
  onClose,
  permanent,
  showClear,
  value,
  wrapperClassName = '',
  dataTestId,
  fluid,
}: {
  id: string;
  label: string;
  setValue: (key: string, value: string) => void;
  hideIcon?: boolean;
  onClose?: () => void;
  permanent?: boolean;
  showClear?: boolean;
  value?: string;
  wrapperClassName?: string;
  dataTestId?: string;
  /**
   * Stretch to the container and use its width: full-width track, value
   * pinned to the right rail, compact chrome. For vertical form layouts
   * (the canvas config panel); the default keeps the filter-bar chip
   * exactly as it always rendered.
   */
  fluid?: boolean;
}) => {
  const minValue = 0;
  const maxValue = 9;
  const [riskScore, setRiskScore] = useState(minValue);

  useEffect(() => {
    setRiskScore(Number(value) || minValue);
  }, [value]);

  if (fluid) {
    // The fill tracks the thumb exactly (score/9), which the chip's
    // 10%-bucket classes cannot do.
    const fillPercent = Math.round((riskScore / maxValue) * 100);
    let fillColor = '#eab308';
    if (riskScore > 7) {
      fillColor = '#dc2626';
    } else if (riskScore > 3) {
      fillColor = '#f97316';
    }
    let fillStyle = {};
    if (riskScore > minValue) {
      fillStyle = {
        background: `linear-gradient(to right, ${fillColor} 0%, ${fillColor} ${fillPercent}%, transparent ${fillPercent}%)`,
      };
    }
    return (
      <div
        data-testid={dataTestId}
        className={`flex h-9 w-full items-center gap-3 rounded-md border border-gray-300 bg-white px-3 text-sm antialiased dark:border-gray-600 dark:bg-gray-900 ${wrapperClassName}`}
      >
        {label !== '' && (
          <span className="shrink-0 font-medium text-black dark:text-white">
            {label}
          </span>
        )}
        <GreaterThanEqualsIcon className="h-4 w-4 shrink-0 text-gray-800 dark:text-gray-200" />
        <div className="slider min-w-0 flex-1">
          <input
            className="w-full rounded-full bg-gray-200 bg-no-repeat dark:bg-gray-700"
            style={fillStyle}
            max={maxValue}
            min={minValue}
            onChange={(event) => {
              setRiskScore(Number(event.currentTarget.value) || minValue);
            }}
            onMouseUp={(event) => {
              setValue(id, event.currentTarget.value);
            }}
            onTouchEnd={(event) => {
              setValue(id, event.currentTarget.value);
            }}
            step={1}
            type="range"
            value={riskScore}
          />
        </div>
        <span className="ml-auto w-5 shrink-0 text-right text-sm font-semibold text-gray-800 tabular-nums dark:text-gray-200">
          {riskScore}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid={dataTestId}
      className={`flex h-[40px] flex-grow-0 justify-between gap-2 rounded-md border border-gray-300 bg-white text-sm font-normal text-gray-600 antialiased focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 ${wrapperClassName}`}
    >
      <div className="flex items-center justify-center">
        {!hideIcon && <VariableIcon className="ml-2 h-4 w-4" />}{' '}
        {label !== '' && (
          <span
            className={`font-medium text-black dark:text-white ${
              hideIcon ? 'ml-2' : ''
            }`}
          >
            {label}
          </span>
        )}{' '}
        <GreaterThanEqualsIcon className="h-7 w-7 pt-1 text-gray-800 dark:text-gray-200" />
        <div className="slider flex w-[150px] flex-row items-center gap-2">
          <div className="relative">
            <input
              className={`w-full bg-no-repeat ${getSliderColor(riskScore)}`}
              max={maxValue}
              min={minValue}
              onChange={(event) => {
                setRiskScore(Number(event.currentTarget.value) || minValue);
              }}
              onMouseUp={(event) => {
                setValue(id, event.currentTarget.value);
              }}
              onTouchEnd={(event) => {
                setValue(id, event.currentTarget.value);
              }}
              step={1}
              type="range"
              value={riskScore}
            />
          </div>
          <div className="w-4 p-1 text-sm font-semibold text-gray-800 dark:text-gray-200">
            {riskScore}
          </div>
        </div>
      </div>
      {onClose && !permanent && (
        <button
          className="flex items-center self-stretch justify-self-stretch rounded-r-md px-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
          onClick={onClose}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
      {showClear && riskScore > minValue && (
        <button
          className="flex items-center self-stretch justify-self-stretch rounded-r-md px-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
          onClick={() => {
            setValue(id, '');
          }}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

function getSliderColor(score: number) {
  // bg-[length:10%_100%]
  // bg-[length:20%_100%]
  // bg-[length:30%_100%]
  // bg-[length:40%_100%]
  // bg-[length:50%_100%]
  // bg-[length:60%_100%]
  // bg-[length:70%_100%]
  // bg-[length:80%_100%]
  // bg-[length:90%_100%]
  // bg-[length:100%_100%]

  if (score <= 0) {
    return 'bg-gray-200 dark:bg-gray-700';
  } else if (score <= 3) {
    return `bg-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 bg-[length:${
      score * 10
    }%_100%] bg-gray-200 dark:bg-gray-700`;
  } else if (score <= 7) {
    return `bg-gradient-to-r from-orange-200 via-orange-300 to-orange-400 bg-[length:${
      score * 10
    }%_100%] bg-gray-200 dark:bg-gray-700`;
  } else if (score <= 10) {
    return `bg-gradient-to-r from-red-400 via-red-500 to-red-600 bg-[length:${
      score * 10
    }%_100%] bg-gray-200 dark:bg-gray-700`;
  }
}
